import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Clock, LogIn, LogOut, RefreshCw, Users } from 'lucide-react'
import { toast } from 'sonner'

import { eventsApi } from '@/services/events'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Empty } from '@/components/ui/empty'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'

type EventCheckinPanelProps = {
  jobId: string | number
}

export function EventCheckinPanel({ jobId }: EventCheckinPanelProps) {
  const queryClient = useQueryClient()

  const checkinStatusQuery = useQuery({
    queryKey: ['event-checkin-status', jobId],
    queryFn: () => eventsApi.workday.checkinStatus(jobId),
    staleTime: 15_000,
    refetchInterval: 60_000,
  })

  const ensureEntriesMutation = useMutation({
    mutationFn: () => eventsApi.workday.ensureEntries(jobId),
    onSuccess: () => {
      toast.success('Workday entries prepared')
      queryClient.invalidateQueries({ queryKey: ['event-checkin-status', jobId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Failed to prepare workday entries')
    },
  })

  const syncWorkersMutation = useMutation({
    mutationFn: () => eventsApi.workday.syncWorkers(jobId),
    onSuccess: (result) => {
      toast.success(`Workers synced: ${result.created} added, ${result.updated} updated`)
      queryClient.invalidateQueries({ queryKey: ['event-checkin-status', jobId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Failed to sync workers')
    },
  })

  const checkinMutation = useMutation({
    mutationFn: (workerId: string | number) => eventsApi.workday.checkin(jobId, workerId),
    onSuccess: () => {
      toast.success('Worker checked in')
      queryClient.invalidateQueries({ queryKey: ['event-checkin-status', jobId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Check-in failed')
    },
  })

  const checkoutMutation = useMutation({
    mutationFn: (workerId: string | number) => eventsApi.workday.checkout(jobId, workerId),
    onSuccess: () => {
      toast.success('Worker checked out')
      queryClient.invalidateQueries({ queryKey: ['event-checkin-status', jobId] })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) ?? 'Check-out failed')
    },
  })

  const workers = checkinStatusQuery.data ?? []
  const checkedInCount = workers.filter((w) => w.checked_in).length

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <div>
            <CardTitle className="text-base">Worker Check-in</CardTitle>
            <CardDescription>
              {workers.length > 0
                ? `${checkedInCount} of ${workers.length} workers checked in`
                : 'Track worker attendance for this event'}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => syncWorkersMutation.mutate()}
              disabled={syncWorkersMutation.isPending || ensureEntriesMutation.isPending}
            >
              {syncWorkersMutation.isPending ? (
                <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Users className="mr-2 h-3.5 w-3.5" />
              )}
              Sync Workers
            </Button>
            {!workers.length ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => ensureEntriesMutation.mutate()}
                disabled={ensureEntriesMutation.isPending}
              >
                {ensureEntriesMutation.isPending ? (
                  <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : null}
                Prepare
              </Button>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {checkinStatusQuery.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-xl" />
            ))}
          </div>
        ) : !workers.length ? (
          <Empty className="py-8">
            <Users className="mb-3 h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm font-medium text-foreground">No workers to check in</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Click "Sync Workers" to load assignments, then "Prepare" to set up workday entries.
            </p>
          </Empty>
        ) : (
          <div className="space-y-2">
            {workers.map((worker) => {
              const isWorking = checkinMutation.isPending || checkoutMutation.isPending
              const isCheckedOut = worker.checked_in && worker.checked_out_at != null
              return (
                <div
                  key={String(worker.worker_id)}
                  className="flex items-center justify-between rounded-xl border border-border/60 p-3"
                >
                  <div className="min-w-0 flex items-center gap-3">
                    {isCheckedOut ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-blue-500" />
                    ) : worker.checked_in ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                    ) : (
                      <Clock className="h-4 w-4 shrink-0 text-muted-foreground/60" />
                    )}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {worker.worker_name}
                      </p>
                      {worker.checked_in && worker.checked_in_at ? (
                        <p className="text-xs text-muted-foreground">
                          In {new Date(worker.checked_in_at).toLocaleTimeString()}
                          {worker.checked_out_at ? ` · Out ${new Date(worker.checked_out_at).toLocaleTimeString()}` : ''}
                          {worker.hours != null ? ` · ${Number(worker.hours).toFixed(1)}h` : ''}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs',
                        isCheckedOut
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : worker.checked_in
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : 'bg-muted text-muted-foreground'
                      )}
                    >
                      {isCheckedOut ? 'Done' : worker.checked_in ? 'Checked In' : 'Absent'}
                    </Badge>
                    {!worker.checked_in ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        disabled={isWorking}
                        onClick={() => checkinMutation.mutate(worker.worker_id)}
                      >
                        <LogIn className="mr-1 h-3 w-3" />
                        Check In
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        disabled={isWorking}
                        onClick={() => checkoutMutation.mutate(worker.worker_id)}
                      >
                        <LogOut className="mr-1 h-3 w-3" />
                        Check Out
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
